import React, { useEffect, useMemo, useState } from 'react';
import { StyledPlaces, StyledPlacesContent } from './styled';
import AppBar from '../../../../components/AppBar';
import Layout from '../../../../components/Layout';
import TabsLinks from '../../../../components/TabsLinks';
import { paths } from '../../../../routes';
import Button from '../../../../components/Button';
import Modal from '../../../../components/Modal';
import Icon from '../../../../components/Icon';
import { ApiPlaces } from '../../../../api/places';
import FormPlace from '../../../../forms/FormPlace';
import Loader from '../../../../components/Loader';
import { filterDataByString, findByID, parsePlaces } from '../../../../utils/helpers';
import FormConfirmDelete from '../../../../forms/FormConfirmDelete';
import { useApi } from '../../../../hooks/useApi';
import TablePlaces from './components/TablePlaces';
import Typography from '../../../../components/Typography';
import useTheme from '../../../../hooks/useTheme';
import { toast } from 'react-toastify';
import { useManage } from '../../../../hooks/useManage';
import { OPERATIONS, ROLES } from '../../../../utils/constans';
import { Col, Row } from 'react-styled-flexboxgrid';
import Search from '../../../../components/Search';
import { ApiFiles } from '../../../../api/file';
import { useDispatch, useSelector } from 'react-redux';
import { ApiSuppliers } from '../../../../api/suppliers';
import { setSelectedPlace } from '../../../../redux/places/actions';
import { useTranslation } from 'react-i18next';

const Places = () => {
    const { t } = useTranslation()
    const theme = useTheme();
    const [{ operation, id: activeID }, setState] = useManage();

    const dispatch = useDispatch();

    const [searchShow, setSearchShow] = useState(false);
    const [searchText, setSearchText] = useState("");

    const role = useSelector(state => state.auth?.user?.user?.role?.type);
    const userSupplierPlaceID = useSelector(state => state.auth?.user?.user?.user_supplier?.place_id);
    const placeSelected = useSelector(state => state.places.selected);

    // Setup request
    const [{ data: places, loading: placesLoading }, getPlaces] = useApi(ApiPlaces.get, { manual: true });
    const [{ loading: createPlaceLoading }, createPlace] = useApi(ApiPlaces.create, { manual: true });
    const [{ loading: updatePlaceLoading }, updatePlace] = useApi(ApiPlaces.update, { manual: true });
    const [{ loading: deletePlaceLoading }, deletePlace] = useApi(ApiPlaces.delete, { manual: true });
    const [{ loading: uploadLoading }, upload] = useApi(ApiFiles.upload, { manual: true });
    const [{ data: availableAdminsData, loading: availableAdminsLoading }, getAvailableAdmins] = useApi(ApiSuppliers.getAvailableAdmins, { manual: true });

    useEffect(() => {
        if (role === ROLES.SUPERADMIN) {
            getPlaces();
            getAvailableAdmins();
        } else if (userSupplierPlaceID !== undefined && userSupplierPlaceID !== null) {
            getPlaces({ id: userSupplierPlaceID });
        }
    }, [role, userSupplierPlaceID, getPlaces, getAvailableAdmins])

    // Memoize data
    const placesParsed = useMemo(() => {
        return parsePlaces(places)
    }, [places]);

    const placesFiltered = useMemo(
        () => {
            return filterDataByString(placesParsed, searchText, place => place?.name);
        },
        [placesParsed, searchText]
    );

    const active = useMemo(
        () => placesFiltered && findByID(placesFiltered, activeID),
        [placesFiltered, activeID]
    );

    const availableAdmins = useMemo(
        () => {
            if (availableAdminsData) {
                if (operation === OPERATIONS.EDIT && active?.user_supplier) {
                    return [active.user_supplier, ...availableAdminsData];
                } else {
                    return availableAdminsData;
                }
            }
        },
        [active, operation, availableAdminsData]
    );

    // Click handlers
    const handleClickAdd = () => setState(OPERATIONS.ADD, null);

    const handleClickEdit = id => () => setState(OPERATIONS.EDIT, id);

    const handleClickDelete = id => () => setState(OPERATIONS.DELETE, id);

    const handleClickCancelModal = () => setState(OPERATIONS.READ, null);

    // Submit handlers - only superadmin
    const handleSubmit = async data => {
        const formData = new FormData();
        formData.append("files", data.logo);
        const file = typeof data.logo === 'object' ? await upload(formData) : null;
        const logo = file?.data[0]?.id || null;

        const parsedData = {
            name: data?.name,
            description: data?.description,
            url: data?.url,
            address: data?.location?.address,
            lat: data?.location?.lat,
            lng: data?.location?.lng,
            supplier_id: data.supplier_id
        }

        if (logo) parsedData['logo'] = logo;

        if (operation === OPERATIONS.EDIT) {
            //UPDATE
            const res = await updatePlace(activeID, parsedData);
            if (res?.data) {
                toast.success(t('Obiekt został zaktualizowany'));
                setState(OPERATIONS.READ, null);
                const resPlaces = await getPlaces();
                if (resPlaces?.data) {
                    //Update logo for cmp place select
                    if (parseInt(res?.data?.id, 10) === parseInt(placeSelected?.id, 10)) {
                        const place = places && findByID(resPlaces?.data, placeSelected?.id);
                        dispatch(setSelectedPlace(place));
                    }
                }
                getAvailableAdmins();
            }
        } else {
            //CREATE
            const res = await createPlace(parsedData);
            if (res?.data) {
                toast.success(t('Obiekt został dodany'));
                setState(OPERATIONS.READ, null);
                getPlaces();
                getAvailableAdmins();
            }
        }
    };

    //Only superadmin
    const handleSubmitDelete = async () => {
        //DELETE
        const res = await deletePlace(activeID);
        if (res?.data) {
            toast.success(t('Obiekt został usunięty'));
            setState(OPERATIONS.READ, null);
            getPlaces();
            getAvailableAdmins();
        }
    };

    const handleChangeSearch = e => {
        setSearchText(e.target.value);
    };

    const handleClickSearchIcon = () => {
        setSearchShow(prevState => !prevState);
    };

    return (
        <StyledPlaces>
            <AppBar>
                <Typography
                    font={theme.fonts.mulishRegular}
                    as="h2"
                >
                    {t("Panel administracyjny")}
                </Typography>
            </AppBar>
            <TabsLinks
                tabs={[{
                    to: paths.places,
                    name: t('Obiekty')
                }]}
                rightContent={
                    <Row middle="xs">
                        <Col>
                            <Search
                                value={searchText}
                                onChange={handleChangeSearch}
                                expanded={searchShow}
                                onClickSearchIcon={handleClickSearchIcon}
                            />
                        </Col>
                        {
                            role === ROLES.SUPERADMIN &&
                            <Col>
                                <Button
                                    startIcon={
                                        <Icon
                                            name="add"
                                            width={12}
                                            height={12}
                                        />
                                    }
                                    onClick={handleClickAdd}
                                >
                                    {t("Dodaj obiekt")}
                            </Button>
                            </Col>
                        }
                    </Row>
                }
            />
            <Layout>
                <StyledPlacesContent>
                    <TablePlaces
                        places={placesFiltered}
                        handleClickEdit={handleClickEdit}
                        handleClickDelete={handleClickDelete}
                    />
                </StyledPlacesContent>
            </Layout>
            {
                role === ROLES.SUPERADMIN &&
                <>
                    <Modal
                        open={operation === OPERATIONS.ADD || operation === OPERATIONS.EDIT}
                    >
                        <FormPlace
                            loading={createPlaceLoading || updatePlaceLoading || uploadLoading}
                            edit={operation === OPERATIONS.EDIT}
                            initialValues={operation === OPERATIONS.EDIT ? active?.form_values : {}}
                            place={active}
                            availableAdmins={availableAdmins}
                            onSubmit={handleSubmit}
                            onCancel={handleClickCancelModal}
                        />
                    </Modal>
                    <Modal
                        open={operation === OPERATIONS.DELETE}
                    >
                        <FormConfirmDelete
                            loading={deletePlaceLoading}
                            onSubmit={handleSubmitDelete}
                            onClickCancel={handleClickCancelModal}
                        />
                    </Modal>
                </>
            }
            <Loader show={placesLoading || availableAdminsLoading} />
        </StyledPlaces>
    )
}

export default Places;
